import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import {
  paqueteService,
  Paquete,
  CambiarEstadoPaquete,
  PaqueteServicio,
  DespacharPaquetePayload,
  AgregarServicioPaquetePayload
} from '../../services/paqueteService';
import { sucursalService } from '../../services/sucursalService';

interface PaqueteDetailProps {
  paquete: Paquete;
  onClose: () => void;
}

const PaqueteDetail = ({ paquete: initialPaquete, onClose }: PaqueteDetailProps) => {
  const queryClient = useQueryClient();
  const [showCambiarEstado, setShowCambiarEstado] = useState(false);
  const [nuevoEstado, setNuevoEstado] = useState<string>(initialPaquete.estado);
  const [notasCambio, setNotasCambio] = useState<string>('');
  const [showAgregarServicio, setShowAgregarServicio] = useState(false);
  const [servicioId, setServicioId] = useState<number | undefined>(undefined);
  const [cantidadServicio, setCantidadServicio] = useState<number>(1);
  const [notasServicio, setNotasServicio] = useState<string>('');
  const [numeroGuiaSalida, setNumeroGuiaSalida] = useState('');
  const [paqueteriaSalida, setPaqueteriaSalida] = useState('');
  const [notasDespacho, setNotasDespacho] = useState('');

  // Refetch para obtener datos actualizados
  const { data: paqueteData } = useQuery(
    ['paquete', initialPaquete.id],
    () => paqueteService.getById(initialPaquete.id),
    { initialData: { data: { paquete: initialPaquete } } }
  );

  const { data: trazabilidadData } = useQuery(
    ['paquete-trazabilidad', initialPaquete.id],
    () => paqueteService.getTrazabilidad(initialPaquete.id)
  );

  const { data: sucursalesData } = useQuery(
    'sucursales',
    () => sucursalService.getAll(1, 100)
  );

  const { data: serviciosData } = useQuery(
    ['paquete-servicios', initialPaquete.id],
    () => paqueteService.getServicios(initialPaquete.id)
  );

  const paquete = paqueteData?.data.paquete || initialPaquete;
  const trazabilidad = trazabilidadData?.data.trazabilidad || paquete.trazabilidad || [];
  const serviciosAplicados: PaqueteServicio[] = serviciosData?.data.servicios || [];

  const cambiarEstadoMutation = useMutation(
    (data: CambiarEstadoPaquete) => paqueteService.cambiarEstado(paquete.id, data),
    {
      onSuccess: () => {
        toast.success('Estado del paquete actualizado correctamente');
        queryClient.invalidateQueries(['paquete', paquete.id]);
        queryClient.invalidateQueries(['paquetes']);
        queryClient.invalidateQueries(['paquete-trazabilidad', paquete.id]);
        setShowCambiarEstado(false);
        setNotasCambio('');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al cambiar estado del paquete');
      }
    }
  );

  const agregarServicioMutation = useMutation(
    (data: AgregarServicioPaquetePayload) => paqueteService.agregarServicio(paquete.id, data),
    {
      onSuccess: () => {
        toast.success('Servicio agregado correctamente');
        queryClient.invalidateQueries(['paquete-servicios', paquete.id]);
        setShowAgregarServicio(false);
        setServicioId(undefined);
        setCantidadServicio(1);
        setNotasServicio('');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al agregar servicio');
      }
    }
  );

  const despacharMutation = useMutation(
    (data: DespacharPaquetePayload) => paqueteService.despachar(paquete.id, data),
    {
      onSuccess: () => {
        toast.success('Paquete despachado correctamente');
        queryClient.invalidateQueries(['paquete', paquete.id]);
        queryClient.invalidateQueries(['paquetes']);
        onClose();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al despachar paquete');
      }
    }
  );

  const handleCambiarEstado = () => {
    if (nuevoEstado === paquete.estado) {
      toast.info('El estado no ha cambiado');
      return;
    }

    cambiarEstadoMutation.mutate({
      estado: nuevoEstado as any,
      notas: notasCambio || undefined
    });
  };

  const handleAgregarServicio = () => {
    if (!servicioId) {
      toast.error('Debes seleccionar un servicio');
      return;
    }
    agregarServicioMutation.mutate({
      servicioId,
      cantidad: cantidadServicio || 1,
      notas: notasServicio || undefined
    });
  };

  const handleDespachar = () => {
    if (!numeroGuiaSalida || !paqueteriaSalida) {
      toast.error('Número de guía y paquetería de salida son obligatorios');
      return;
    }
    despacharMutation.mutate({
      numeroGuiaSalida,
      paqueteriaSalida,
      notas: notasDespacho || undefined
    });
  };

  const getEstadoColor = (estado: string) => {
    const colors: { [key: string]: string } = {
      registrado: 'bg-gray-100 text-gray-800',
      en_almacen: 'bg-blue-100 text-blue-800',
      en_transito: 'bg-yellow-100 text-yellow-800',
      entregado: 'bg-green-100 text-green-800',
      devuelto: 'bg-orange-100 text-orange-800',
      cancelado: 'bg-red-100 text-red-800'
    };
    return colors[estado] || 'bg-gray-100 text-gray-800';
  };

  const getTipoColor = (tipo: string) => {
    return tipo === 'interno' 
      ? 'bg-purple-100 text-purple-800'
      : 'bg-indigo-100 text-indigo-800';
  };

  const estadosDisponibles = [
    { value: 'registrado', label: 'Registrado' },
    { value: 'en_almacen', label: 'En Almacén' },
    { value: 'en_transito', label: 'En Tránsito' },
    { value: 'entregado', label: 'Entregado' },
    { value: 'devuelto', label: 'Devuelto' },
    { value: 'cancelado', label: 'Cancelado' },
    { value: 'recibido_centro', label: 'Recibido en Centro' },
    { value: 'en_servicio', label: 'En Servicio' },
    { value: 'preparado_salida', label: 'Preparado para Salida' },
    { value: 'despachado', label: 'Despachado' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold">Paquete {paquete.numeroTracking}</h2>
            {paquete.codigoBarras && (
              <p className="text-sm text-gray-500">Código: {paquete.codigoBarras}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ✕
          </button>
        </div>

        <div className="space-y-6">
          {/* Información General */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600">Estado</p>
              <span className={`px-2 py-1 text-xs rounded-full ${getEstadoColor(paquete.estado)}`}>
                {paquete.estado.replace('_', ' ').toUpperCase()}
              </span>
            </div>
            <div>
              <p className="text-sm text-gray-600">Tipo</p>
              <span className={`px-2 py-1 text-xs rounded-full ${getTipoColor(paquete.tipo)}`}>
                {paquete.tipo.toUpperCase()}
              </span>
            </div>
            <div>
              <p className="text-sm text-gray-600">Días en Almacén</p>
              <p className={`font-medium ${paquete.diasAlmacenaje >= 7 ? 'text-red-600' : ''}`}>
                {paquete.diasAlmacenaje} días
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Sucursal</p>
              <p className="font-medium">{paquete.sucursal?.name || '-'}</p>
            </div>
            {paquete.numeroGuiaEntrada && (
              <div>
                <p className="text-sm text-gray-600">Guía Entrada</p>
                <p className="font-medium">{paquete.numeroGuiaEntrada}</p>
                {paquete.paqueteriaEntrada && (
                  <p className="text-xs text-gray-500">{paquete.paqueteriaEntrada}</p>
                )}
              </div>
            )}
            {paquete.numeroGuiaSalida && (
              <div>
                <p className="text-sm text-gray-600">Guía Salida</p>
                <p className="font-medium">{paquete.numeroGuiaSalida}</p>
                {paquete.paqueteriaSalida && (
                  <p className="text-xs text-gray-500">{paquete.paqueteriaSalida}</p>
                )}
              </div>
            )}
            {paquete.montoAlmacenaje > 0 && (
              <div>
                <p className="text-sm text-gray-600">Cargo por Almacenaje</p>
                <p className="font-medium text-primary-700">
                  ${Number(paquete.montoAlmacenaje).toFixed(2)}
                </p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-600">Fecha Entrada</p>
              <p className="font-medium">{new Date(paquete.fechaEntrada).toLocaleString()}</p>
            </div>
            {paquete.fechaSalidaReal && (
              <div>
                <p className="text-sm text-gray-600">Fecha Salida</p>
                <p className="font-medium">{new Date(paquete.fechaSalidaReal).toLocaleString()}</p>
              </div>
            )}
          </div>

          {/* Remitente y Destinatario */}
          <div className="grid grid-cols-2 gap-4 border-t pt-4">
            <div>
              <h3 className="font-semibold mb-2">Remitente</h3>
              <p className="text-sm"><strong>Nombre:</strong> {paquete.remitenteNombre}</p>
              {paquete.remitenteTelefono && (
                <p className="text-sm"><strong>Teléfono:</strong> {paquete.remitenteTelefono}</p>
              )}
              {paquete.remitenteEmail && (
                <p className="text-sm"><strong>Email:</strong> {paquete.remitenteEmail}</p>
              )}
              {paquete.remitenteDireccion && (
                <p className="text-sm"><strong>Dirección:</strong> {paquete.remitenteDireccion}</p>
              )}
            </div>
            <div>
              <h3 className="font-semibold mb-2">Destinatario</h3>
              <p className="text-sm"><strong>Nombre:</strong> {paquete.destinatarioNombre}</p>
              <p className="text-sm"><strong>Teléfono:</strong> {paquete.destinatarioTelefono}</p>
              {paquete.destinatarioEmail && (
                <p className="text-sm"><strong>Email:</strong> {paquete.destinatarioEmail}</p>
              )}
              <p className="text-sm"><strong>Dirección:</strong> {paquete.destinatarioDireccion}</p>
            </div>
          </div>

          {/* Información Física */}
          {(paquete.peso || paquete.dimensiones || paquete.valorDeclarado) && (
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-2">Información Física</h3>
              <div className="grid grid-cols-3 gap-4">
                {paquete.peso && (
                  <div>
                    <p className="text-sm text-gray-600">Peso</p>
                    <p className="font-medium">{paquete.peso} kg</p>
                  </div>
                )}
                {paquete.dimensiones && (
                  <div>
                    <p className="text-sm text-gray-600">Dimensiones</p>
                    <p className="font-medium">{paquete.dimensiones}</p>
                  </div>
                )}
                {paquete.valorDeclarado && (
                  <div>
                    <p className="text-sm text-gray-600">Valor Declarado</p>
                    <p className="font-medium">${(typeof paquete.valorDeclarado === 'string' ? parseFloat(paquete.valorDeclarado) : (paquete.valorDeclarado || 0)).toFixed(2)}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notas */}
          {paquete.notas && (
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-2">Notas</h3>
              <p className="text-sm text-gray-700">{paquete.notas}</p>
            </div>
          )}

          {/* Trazabilidad */}
          <div className="border-t pt-4">
            <h3 className="font-semibold mb-3">Trazabilidad</h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {trazabilidad.length > 0 ? (
                trazabilidad.map((item, index) => (
                  <div key={item.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded">
                    <div className="flex-shrink-0 w-2 h-2 bg-primary-600 rounded-full mt-2"></div>
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-sm">
                            {item.estadoNuevo.replace('_', ' ').toUpperCase()}
                          </p>
                          {item.estadoAnterior && (
                            <p className="text-xs text-gray-500">
                              Anterior: {item.estadoAnterior.replace('_', ' ')}
                            </p>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">
                          {new Date(item.fecha).toLocaleString()}
                        </p>
                      </div>
                      {item.ubicacion && (
                        <p className="text-xs text-gray-600 mt-1">📍 {item.ubicacion}</p>
                      )}
                      {item.sucursal && (
                        <p className="text-xs text-gray-600">🏢 {item.sucursal.name}</p>
                      )}
                      {item.usuario && (
                        <p className="text-xs text-gray-600">👤 {item.usuario.name}</p>
                      )}
                      {item.notas && (
                        <p className="text-xs text-gray-700 mt-1 italic">{item.notas}</p>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">No hay registros de trazabilidad</p>
              )}
            </div>
          </div>

          {/* Servicios aplicados */}
          <div className="border-t pt-4">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-semibold">Servicios Aplicados</h3>
              <button
                onClick={() => setShowAgregarServicio(true)}
                className="px-3 py-1 text-sm bg-primary-600 text-white rounded hover:bg-primary-700"
              >
                + Agregar Servicio
              </button>
            </div>
            {serviciosAplicados.length === 0 ? (
              <p className="text-sm text-gray-500">Este paquete no tiene servicios aplicados.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left">Servicio</th>
                      <th className="px-4 py-2 text-left">Cantidad</th>
                      <th className="px-4 py-2 text-left">Precio Unitario</th>
                      <th className="px-4 py-2 text-left">Total</th>
                      <th className="px-4 py-2 text-left">Fecha</th>
                      <th className="px-4 py-2 text-left">Usuario</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {serviciosAplicados.map((s) => (
                      <tr key={s.id}>
                        <td className="px-4 py-2">{s.servicio?.nombre || s.servicioId}</td>
                        <td className="px-4 py-2">{s.cantidad}</td>
                        <td className="px-4 py-2">${Number(s.precioUnitario).toFixed(2)}</td>
                        <td className="px-4 py-2 font-medium">${Number(s.montoTotal).toFixed(2)}</td>
                        <td className="px-4 py-2">
                          {new Date(s.fecha).toLocaleString()}
                        </td>
                        <td className="px-4 py-2">
                          {s.usuario?.name || s.usuarioId}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Cambiar Estado */}
          {paquete.estado !== 'entregado' && paquete.estado !== 'cancelado' && (
            <div className="border-t pt-4">
              {!showCambiarEstado ? (
                <button
                  onClick={() => setShowCambiarEstado(true)}
                  className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
                >
                  Cambiar Estado
                </button>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nuevo Estado *
                    </label>
                    <select
                      value={nuevoEstado}
                      onChange={(e) => setNuevoEstado(e.target.value)}
                      className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {estadosDisponibles.map((estado) => (
                        <option key={estado.value} value={estado.value}>
                          {estado.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notas (Opcional)
                    </label>
                    <textarea
                      value={notasCambio}
                      onChange={(e) => setNotasCambio(e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Notas sobre el cambio de estado..."
                    />
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleCambiarEstado}
                      disabled={cambiarEstadoMutation.isLoading || nuevoEstado === paquete.estado}
                      className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
                    >
                      {cambiarEstadoMutation.isLoading ? 'Guardando...' : 'Guardar Cambio'}
                    </button>
                    <button
                      onClick={() => {
                        setShowCambiarEstado(false);
                        setNuevoEstado(paquete.estado);
                        setNotasCambio('');
                      }}
                      className="px-4 py-2 border rounded hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Despachar paquete */}
          {paquete.estado !== 'despachado' && paquete.estado !== 'cancelado' && (
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">Despachar Paquete</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número de Guía Salida *
                  </label>
                  <input
                    value={numeroGuiaSalida}
                    onChange={(e) => setNumeroGuiaSalida(e.target.value)}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Paquetería Salida *
                  </label>
                  <input
                    value={paqueteriaSalida}
                    onChange={(e) => setPaqueteriaSalida(e.target.value)}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notas de Despacho
                  </label>
                  <input
                    value={notasDespacho}
                    onChange={(e) => setNotasDespacho(e.target.value)}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <div className="mt-3">
                <button
                  onClick={handleDespachar}
                  disabled={despacharMutation.isLoading}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  {despacharMutation.isLoading ? 'Despachando...' : 'Despachar Paquete'}
                </button>
              </div>
            </div>
          )}

          <div className="flex justify-end pt-4 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>

      {/* Modal agregar servicio */}
      {showAgregarServicio && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-3">Agregar Servicio al Paquete</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Servicio *
                </label>
                <select
                  value={servicioId || ''}
                  onChange={(e) => setServicioId(e.target.value ? Number(e.target.value) : undefined)}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Selecciona un servicio</option>
                  {/* En esta primera versión, el catálogo de servicios se podría cargar en otra pantalla;
                      aquí asumimos que conoces los IDs o se mejorará más adelante. */}
                  <option value={1}>Reempaque</option>
                  <option value={2}>Entarimado</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cantidad
                </label>
                <input
                  type="number"
                  min={1}
                  value={cantidadServicio}
                  onChange={(e) => setCantidadServicio(Number(e.target.value) || 1)}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notas
                </label>
                <textarea
                  value={notasServicio}
                  onChange={(e) => setNotasServicio(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex justify-end space-x-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAgregarServicio(false)}
                  className="px-4 py-2 border rounded hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleAgregarServicio}
                  disabled={agregarServicioMutation.isLoading}
                  className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
                >
                  {agregarServicioMutation.isLoading ? 'Guardando...' : 'Agregar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaqueteDetail;

